import { useState, useEffect } from "react";
import { formatCurrencyPortal } from "../../../utils/FormatUtils";
import { TranslationService } from "../../../services/TranslationService";
import "./PaymentMethodModal.css";

type PaymentProvider = {
  id: string;
  name: string;
  logo?: string;
};

type PaymentMethodModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onNext: (selectedProviderId: string) => void;
  providers: PaymentProvider[];
  loading?: boolean;
  totalAmount?: number;
  currencySymbol?: string;
  selectedCount?: number;
  showCents?: boolean;
  hasMultipleCurrencies?: boolean;
};

export const PaymentMethodModal = ({
  isOpen,
  onClose,
  onNext,
  providers,
  loading = false,
  totalAmount = 0,
  currencySymbol = "",
  selectedCount = 0,
  showCents = false,
  hasMultipleCurrencies = false
}: PaymentMethodModalProps) => {
  const [selectedProvider, setSelectedProvider] = useState<string | null>(null);

  // Resetear selección cuando se abre el modal
  // Si hay un solo proveedor, seleccionarlo automáticamente
  useEffect(() => {
    if (isOpen) {
      if (providers.length === 1) {
        setSelectedProvider(providers[0].id);
      } else {
        setSelectedProvider(null);
      }
    }
  }, [isOpen, providers]);

  const handleNext = () => {
    if (selectedProvider) {
      onNext(selectedProvider);
    }
  };

  const handleProviderSelect = (providerId: string) => {
    setSelectedProvider(providerId);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <>
      <div className="payment-modal-backdrop" onClick={onClose}></div>
      <div className="payment-modal">
        <div className="payment-modal-header">
          <h3 className="payment-modal-title">Elige como pagar</h3>
          <button
            className="payment-modal-close"
            onClick={onClose}
            aria-label="Cerrar"
          >
            <i className="fa-light fa-times"></i>
          </button>
        </div>

        <div className="payment-modal-body">
          {/* Resumen de facturas seleccionadas */}
          {selectedCount > 0 && (
            <div className="payment-modal-summary">
              <div className="payment-modal-summary-info">
                {hasMultipleCurrencies ? (
                  <span className="payment-modal-summary-error">
                    <i className="fa-light fa-exclamation-triangle me-2"></i>
                    No se pueden pagar facturas con diferentes monedas
                  </span>
                ) : (
                  <span className="payment-modal-summary-text">
                    {selectedCount}{" "}
                    {selectedCount === 1
                      ? "Factura seleccionada"
                      : "Facturas seleccionadas"}{" "}
                    {totalAmount > 0
                      ? `${TranslationService.translate.By} ${formatCurrencyPortal(totalAmount, currencySymbol, showCents)}`
                      : ""}
                  </span>
                )}
              </div>
            </div>
          )}

          {loading ? (
            <div className="payment-modal-empty">
              <p>Cargando métodos de pago...</p>
            </div>
          ) : providers.length === 0 ? (
            <div className="payment-modal-empty">
              <p>No hay métodos de pago disponibles</p>
            </div>
          ) : (
            <div className="payment-providers-list">
              {providers.map((provider) => (
                <div
                  key={provider.id}
                  className={`payment-provider-option ${selectedProvider === provider.id ? "selected" : ""}`}
                  onClick={() => handleProviderSelect(provider.id)}
                >
                  {provider.logo ? (
                    <img
                      src={provider.logo}
                      alt={provider.name}
                      className="payment-provider-logo"
                    />
                  ) : (
                    <div className="payment-provider-name">{provider.name}</div>
                  )}
                  {selectedProvider === provider.id && (
                    <div className="payment-provider-check">
                      <i className="fa-solid fa-check"></i>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="payment-modal-footer">
          <button
            className="btn btn-payment-next"
            onClick={handleNext}
            disabled={
              !selectedProvider || hasMultipleCurrencies || totalAmount === 0
            }
          >
            Siguiente
          </button>
        </div>
      </div>
    </>
  );
};
